import {
	createStore,
	AppmakerFormProvider,
	useAppmakerFormStore,
} from '@appmaker/core/components/AppmakerForm/store';
import AppmakerForm from '@appmaker/core/components/AppmakerForm/Form';
import { SaveIcon } from '@heroicons/react/solid';
import { useEffect, useState } from 'react';
import LoadingIcon from '@appmaker/components/LoadingIcon';
import { useParams } from '@appmaker/core/routes';
import { useExtensionDetail } from '../store/details';
import { isEqual, size } from 'lodash';
import { UPDATE_SETTINGS } from '../api/graphql';
import { useMutation } from '@apollo/client';
import {
	sendNotification,
	toast,
} from '@appmaker/components/ReactHotToastIntegrator';
const SaveButton = ({ loading, onSaveSchema = () => {} }) => {
	return (
		<button
			type="button"
			disabled={loading}
			className="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
			onClick={() => {
				onSaveSchema();
			}}
		>
			{loading ? (
				<>
					<LoadingIcon
						className="-ml-0.5 h-6 w-6 animate-spin"
						aria-hidden="true"
					/>
					Saving...
				</>
			) : (
				<>
					<SaveIcon className="-ml-0.5 h-6 w-6" aria-hidden="true" />
					Save Changes
				</>
			)}
		</button>
	);
};
const Form = () => {
	const { handle, projectId } = useParams();
	const extension = useExtensionDetail();
	const syncSettings = useExtensionDetail((state) => state.syncVersion);
	const unsaved = useExtensionDetail((state) => state.unSaved);
	const setUnsaved = useExtensionDetail((state) => state.setUnsaved);
	// const storeApi = useAppmakerFormStoreApi();
	const { formData } = useAppmakerFormStore();
	const details = extension?.extensionInstalls?.find(
		(details) => details?.extension?.id === extension?.extension?.id
	);
	const [updateSettings, { data, error, loading }] = useMutation(
		UPDATE_SETTINGS,
		{
			context: {
				headers: {
					'x-appmaker-project-id': projectId,
				},
			},
		}
	);
	// const { formData } = storeApi.getState();
	const onSaveSchema = async () => {
		try {
			const data = JSON.parse(JSON.stringify(formData));
			toast.promise(
				(async () => {
					await updateSettings({
						variables: {
							where: {
								id: details?.id,
							},
							data: {
								androidVersion: data?.androidVersion,
								iosVersion: data?.iosVersion,
							},
						},
					});
					syncSettings(details?.id, data);
				})(),
				{
					loading: 'Saving...',
					success: <b>Settings saved!</b>,
					error: <b>Unable to save</b>,
				}
			);
		} catch (error) {
			sendNotification('Unable to save', 'error');
		}
	};
	useEffect(() => {
		const data = {
			androidVersion: details?.androidVersion ?? '*',
			iosVersion: details?.iosVersion ?? '*',
		};

		if (
			formData &&
			data &&
			!isEqual(JSON.parse(JSON.stringify(formData)), data)
		) {
			setUnsaved(true);
		} else {
			setUnsaved(false);
		}
	}, [formData, details, setUnsaved]);
	const schema = {
		properties: {
			androidVersion: {
				type: 'string',
				label: 'Android version',
				description: "Version like '<20 AND >10'",
			},
			iosVersion: {
				type: 'string',
				label: 'iOS version',
				description: "Version like '>20'",
			},
		},
	};
	if (!details) return null;
	return (
		<div className="m-4 bg-white max-w-3xl p-4 shadow-sm rounded-lg mx-auto">
			<div className="mb-4 pb-4 border-b flex items-end justify-between">
				<div>
					<h1 className="text-xl font-semibold">Version</h1>
					<p className="text-gray-500 text-sm">
						Configure{' '}
						<span className="lowercase">{details?.extension?.name}</span>{' '}
						extension version.
					</p>
				</div>
				{unsaved && (
					<SaveButton loading={loading} onSaveSchema={onSaveSchema} />
				)}
			</div>
			<>
				<AppmakerForm
					formVersion={2}
					// value={{ formData: flatten(formData) }}
					schema={schema ?? {}}
				/>
				<div className="pt-3">
					<SaveButton loading={loading} onSaveSchema={onSaveSchema} />
				</div>
			</>
		</div>
	);
};

const FormWrapper = (props) => {
	const extension = useExtensionDetail();
	const details = extension?.extensionInstalls?.find(
		(details) => details?.extension?.id === extension?.extension?.id
	);

	if (extension?.isLoading) {
		return null;
	}

	return (
		<AppmakerFormProvider
			createStore={createStore({
				formData: {
					androidVersion: details?.androidVersion ?? '*',
					iosVersion: details?.iosVersion ?? '*',
				},
			})}
		>
			<Form {...props} />
		</AppmakerFormProvider>
	);
};
export default FormWrapper;
